import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  placement?: 'top' | 'bottom';
}

const tooltipStyle = {
  whiteSpace: 'pre-line',
  maxWidth: '24rem',
  minWidth: '16rem',
  padding: '1rem',
  background: '#111827',
  color: '#fff',
  borderRadius: '0.75rem',
  fontSize: '0.75rem',
  boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
  wordBreak: 'normal' as 'normal',
  overflowX: 'auto' as 'auto',
  border: '1px solid #22223b',
};

const Tooltip: React.FC<TooltipProps> = ({ text, children, placement = 'bottom' }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0, align: 'center' });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (visible && tooltipRef.current && containerRef.current) {
      const tooltip = tooltipRef.current;
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let x = containerRect.left + containerRect.width / 2 - tooltipRect.width / 2;
      let y = placement === 'top' 
        ? containerRect.top - tooltipRect.height - 8
        : containerRect.bottom + 8;
      
      let align = 'center';
      
      // Adjust horizontal position if tooltip would be cut off
      if (x + tooltipRect.width > viewportWidth - 20) {
        x = containerRect.right - tooltipRect.width;
        align = 'right';
      } else if (x < 20) {
        x = containerRect.left;
        align = 'left';
      }
      
      // Adjust vertical position if tooltip would be cut off
      if (y + tooltipRect.height > viewportHeight - 20) {
        y = containerRect.top - tooltipRect.height - 8;
      } else if (y < 20) {
        y = containerRect.bottom + 8;
      }
      
      setPosition({ x, y, align });
    }
  }, [visible, placement]);

  const tooltipElement = visible ? (
    <div
      ref={tooltipRef}
      className="fixed z-[9999]"
      style={{
        ...tooltipStyle,
        left: position.x,
        top: position.y,
      }}
    >
      {text}
    </div>
  ) : null;

  return (
    <>
      <span 
        ref={containerRef}
        className="relative inline-block"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        tabIndex={0}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
      >
        {children}
      </span>
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  );
};

export default Tooltip; 